import { publicClient } from "@/api/publicClient";
import { API_ROUTES } from "@/api/endpoints";
import { privateClient } from "@/api";
import { CSRF_HEADER_NAME, REFRESH_HEADER_NAME, getCsrfToken, getRefreshToken } from "../csrfToken";

export interface LoginPayload {
  email: string;
  password: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  isFirstLogin: boolean;
  role: {
    id: number;
    name: string;
    scope: string;
  };
  organizationId: number | null;
  campusId: number | null;
  institutionId: number | null;
  /** The institution's IANA time zone (set by the super admin); null for logins that belong to no institution. */
  institutionTimezone?: string | null;
  features: string[];
  /** What this login may do in each feature, e.g. { fees: ["view", "collect"] }. Admin logins have no entry and may do everything. */
  permissions?: Record<string, string[]>;
  /** Modules enabled for this user's institution/organization; null/absent means unrestricted. */
  modules?: string[] | null;
}

export interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
  csrfToken: string;
}

export const loginRequest = async (payload: LoginPayload): Promise<LoginResponse> => {
  const { data } = await publicClient.post(API_ROUTES.AUTH_LOGIN, payload);
  return data;
};

// Tenant admins only (ORG/CAMPUS/INSTITUTION) — the backend rejects any
// other account's Google Sign-In outright, see authService.loginWithGoogle.
export const googleLoginRequest = async (idToken: string): Promise<LoginResponse> => {
  const { data } = await publicClient.post(API_ROUTES.AUTH_GOOGLE, { idToken });
  return data;
};


export interface LogoutResult {
  ok: boolean;
}

export const logoutRequest = async (): Promise<LogoutResult> => {
  // /auth/logout is CSRF-protected — see backend/src/middleware/csrf.js.
  const { data } = await privateClient.post(
    API_ROUTES.AUTH_LOGOUT,
    undefined,
    {
      headers: {
        [CSRF_HEADER_NAME]: getCsrfToken() ?? "",
        [REFRESH_HEADER_NAME]: getRefreshToken() ?? "",
      },
    }
  );
  return data;
};

export interface ChangePasswordPayload {
  currentPassword?: string;
  newPassword: string;
}

export interface ChangePasswordResult {
  ok: boolean;
}

export const changePasswordRequest = async (payload: ChangePasswordPayload): Promise<ChangePasswordResult> => {
  const { data } = await privateClient.post(API_ROUTES.AUTH_CHANGE_PASSWORD, payload);
  return data;
};
export interface MyAccess {
  modules: string[] | null;
  features: string[];
  permissions?: Record<string, string[]>;
}

/** The modules and features this login can use right now. Login returns the same lists, but they go stale when an admin changes a module. */
export const fetchMyAccess = async (): Promise<MyAccess> => {
  const { data } = await privateClient.get(API_ROUTES.AUTH_ME_ACCESS);
  return data;
};
